import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
    realtimeService,
    type RealtimeChannel,
    type RealtimeEvent,
} from "../services/realtimeService";

/**
 * Generischer Echtzeit-Client (Server-Sent Events).
 *
 * Öffnet EINE EventSource auf `/api/realtime/events` (nur bei eingeloggtem
 * User; schließt bei Logout) und verteilt eingehende Frames an Konsumenten,
 * die sich per `subscribe(channel, handler)` anmelden. Damit können
 * NavigationLayout (Glocke, channel 'notification') und später offene
 * Notiz-/Kommentar-Threads (channel 'note') entkoppelt mithören, ohne
 * jeweils eigene Streams zu öffnen.
 *
 * Blaupause: TelephonyContext (esRef / onopen / onerror / onmessage→JSON.parse).
 * Bewusst KEIN WebSocket. Single-Pod-Bus auf Serverseite → 'ping'-Heartbeats
 * werden ignoriert; das jeweilige Polling bleibt als Fallback bestehen.
 */

type Handler = (evt: RealtimeEvent) => void;

interface RealtimeContextValue {
    /** SSE-Stream offen + authentifiziert. */
    isConnected: boolean;
    /** Auf einen Channel hören; gibt eine Abmelde-Funktion zurück. */
    subscribe: (channel: RealtimeChannel, handler: Handler) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

export const useRealtime = (): RealtimeContextValue => {
    const ctx = useContext(RealtimeContext);
    if (!ctx) throw new Error("useRealtime must be used within a RealtimeProvider");
    return ctx;
};

/**
 * Wie {@link useRealtime}, wirft aber NICHT, wenn kein Provider darüber liegt
 * (gibt dann `undefined`). Für Komponenten wie NoteChat, die in der App stets
 * unter dem Provider laufen, aber defensiv keinen Fehler werfen sollen, falls
 * sie isoliert (z. B. im Test) gerendert werden.
 */
export const useRealtimeOptional = (): RealtimeContextValue | undefined =>
    useContext(RealtimeContext);

interface RealtimeProviderProps {
    children: ReactNode;
}

// SP3 — Staleness-Heilung bei Tab-Rückkehr/Wake:
// Der Server pingt alle 25s (siehe realtime.controller). Bleibt jeder Frame
// (inkl. Ping) länger als 2,5 Pings (~62,5s) aus, gilt der Stream als still
// verhungert (Proxy-Idle, Tab-Suspend, Laptop-Standby) → harter Neuaufbau.
const STALE_MS = 62_500;
// Niederfrequenter Wächter — fängt den Kernfall „Laptop-Standby bei SICHTBAREM
// Tab", den visibilitychange/focus nicht sehen.
const STALE_CHECK_INTERVAL_MS = 60_000;
// Erst nach dieser Hintergrund-Dauer heilt die Tab-Rückkehr AKTIV. Kurzes
// Alt-Tab (< Schwelle) löst nichts aus → schont Cache/Graph-Last, vermeidet
// Multi-Tab-Stampede.
const HIDDEN_RESYNC_MS = 3 * 60_000;

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const esRef = useRef<EventSource | null>(null);
    // B8: merkt einen Verbindungsabriss, um beim Wiederaufbau einmal zu heilen.
    const hadDisconnectRef = useRef(false);
    // SP3: Zeitpunkt des letzten eingegangenen Frames (inkl. Server-Ping). Wird in
    // `dispatch` gesetzt; der Staleness-Wächter vergleicht dagegen.
    const lastFrameAtRef = useRef<number>(Date.now());
    // SP3: Beginn der letzten „hidden"-Phase (Tab im Hintergrund); null, solange der
    // Tab sichtbar ist. Nur eine Abwesenheit > HIDDEN_RESYNC_MS heilt aktiv.
    const hiddenSinceRef = useRef<number | null>(null);
    // Client-seitiges Pub/Sub: channel → Set<Handler>.
    const handlersRef = useRef<Map<RealtimeChannel, Set<Handler>>>(new Map());

    const subscribe = useCallback((channel: RealtimeChannel, handler: Handler) => {
        let set = handlersRef.current.get(channel);
        if (!set) {
            set = new Set();
            handlersRef.current.set(channel, set);
        }
        set.add(handler);
        return () => {
            handlersRef.current.get(channel)?.delete(handler);
        };
    }, []);

    const dispatch = useCallback((evt: RealtimeEvent) => {
        // SP3: JEDER Frame belegt, dass der Stream lebt — VOR dem Ping-Ignore
        // setzen, damit auch der 25s-Heartbeat die Frische mitträgt.
        lastFrameAtRef.current = Date.now();
        if (evt.channel === "ping") return; // Heartbeat ignorieren
        const set = handlersRef.current.get(evt.channel);
        if (!set) return;
        set.forEach((h) => {
            try {
                h(evt);
            } catch {
                /* einzelner Handler-Fehler darf den Stream nicht killen */
            }
        });
    }, []);

    useEffect(() => {
        const closeStream = () => {
            if (esRef.current) {
                esRef.current.close();
                esRef.current = null;
            }
            setIsConnected(false);
        };

        if (!isAuthenticated) {
            closeStream();
            return closeStream;
        }

        // (Neu-)Aufbau des Streams. Wird für den Erst-Connect UND für den aktiven
        // Heil-Neuaufbau (Tab-Rückkehr/Wake/online) verwendet; die onopen-Logik
        // feuert dann über hadDisconnectRef den bestehenden reconnect-Pfad (B8).
        const openStream = () => {
            if (esRef.current) {
                esRef.current.close();
                esRef.current = null;
            }
            const es = realtimeService.openEventStream();
            if (!es) return;
            esRef.current = es;
            // Frisch ab dem Öffnen — verhindert einen sofortigen Fehl-Restart des
            // Staleness-Wächters, bevor der erste Frame/Ping eintrifft.
            lastFrameAtRef.current = Date.now();

            es.onopen = () => {
                // B8: Nach einem Verbindungsabriss sind Frames verloren (In-Memory-Bus,
                // kein Replay). Ein synthetisches `reconnect`-Event lässt Konsumenten
                // (MailContext) ihre Caches invalidieren und den Stand aufholen.
                if (hadDisconnectRef.current) {
                    hadDisconnectRef.current = false;
                    dispatch({
                        tenantId: "",
                        userId: "",
                        channel: "reconnect",
                        type: "reconnected",
                        payload: {},
                    } as RealtimeEvent);
                }
                setIsConnected(true);
            };
            es.onerror = () => {
                // EventSource reconnectet bei transienten Fehlern automatisch.
                hadDisconnectRef.current = true;
                setIsConnected(false);
            };
            es.onmessage = (ev) => {
                let evt: RealtimeEvent;
                try {
                    evt = JSON.parse(ev.data) as RealtimeEvent;
                } catch {
                    return;
                }
                dispatch(evt);
            };
        };

        openStream();

        // SP3: Harter Heil-Neuaufbau — Stream schließen + neu öffnen; hadDisconnectRef
        // sorgt dafür, dass der bestehende onopen-Pfad den synthetischen reconnect-
        // Frame feuert (B8-Mechanik 1:1 wiederverwendet, nicht dupliziert).
        const restartStream = () => {
            hadDisconnectRef.current = true;
            setIsConnected(false);
            openStream();
        };

        // SP3: Nur heilen, wenn der Stream nachweislich verhungert ist (kein Frame/
        // Ping seit > STALE_MS) — schützt vor Restart-Stampede bei kurzem Alt-Tab.
        const restartIfStale = () => {
            if (Date.now() - lastFrameAtRef.current > STALE_MS) restartStream();
        };

        const onVisibility = () => {
            if (document.visibilityState === "hidden") {
                hiddenSinceRef.current = Date.now();
                return;
            }
            const hiddenSince = hiddenSinceRef.current;
            hiddenSinceRef.current = null;
            // Tab wieder sichtbar: AKTIV heilen nur nach längerer Abwesenheit; kurzes
            // Alt-Tab → nichts (Cache/Graph-Last schonen).
            if (hiddenSince != null && Date.now() - hiddenSince > HIDDEN_RESYNC_MS) {
                restartStream();
            }
        };
        // Fokus-Rückkehr heilt NUR bei nachweislich totem Stream (Staleness-Gate) →
        // kurzes Alt-Tab bleibt folgenlos.
        const onFocus = () => restartIfStale();
        // Netz zurück → der bisherige Stream ist mit Sicherheit tot.
        const onOnline = () => restartStream();

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", onFocus);
        window.addEventListener("online", onOnline);
        // Niederfrequenter Wächter — Kernfall Laptop-Standby bei sichtbarem Tab.
        const staleTimer = window.setInterval(restartIfStale, STALE_CHECK_INTERVAL_MS);

        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("online", onOnline);
            window.clearInterval(staleTimer);
            closeStream();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    return (
        <RealtimeContext.Provider value={{ isConnected, subscribe }}>
            {children}
        </RealtimeContext.Provider>
    );
};
